import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResumeCart() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear, add } = useCart();
  const [state, setState] = useState({ status: "loading", message: "" });

  useEffect(() => {
    const order = params.get("order");
    const token = params.get("token");
    if (!order || !token) {
      setState({ status: "error", message: "Missing resume link parameters." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/cart/resume?order=${order}&token=${token}`);
        if (data.already_paid) {
          setState({ status: "already-paid", message: "This order was already completed." });
          return;
        }
        if (!data.items?.length) {
          setState({ status: "error", message: "The items in your cart are no longer available." });
          return;
        }
        clear();
        // Rehydrate cart (deduplicate qty by product_id)
        for (const it of data.items) {
          add({ id: it.product_id, name: it.name, our_price: it.our_price, condition: it.condition,
                images: it.images, sku: it.sku, quantity: 99 }, it.quantity);
        }
        toast.success(`Restored ${data.items.length} item${data.items.length > 1 ? "s" : ""} to your cart`);
        setTimeout(() => navigate("/checkout"), 800);
        setState({ status: "success", message: "Redirecting to checkout…" });
      } catch (err) {
        const msg = err?.response?.data?.detail || "Sorry, we couldn't restore your cart.";
        setState({ status: "error", message: msg });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = state.status === "loading" ? Loader2 : state.status === "success" ? CheckCircle2 : AlertCircle;
  const iconClass = state.status === "loading" ? "animate-spin" : state.status === "success" ? "text-success" : "text-danger";

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div data-testid="resume-cart-card" className="max-w-md w-full border-2 border-ink bg-paper p-8 text-center shadow-hard">
        <Icon className={`h-10 w-10 mx-auto mb-4 ${iconClass}`} />
        <h1 className="font-heading font-black text-2xl tracking-tighter mb-2">
          {state.status === "loading" ? "RESTORING YOUR CART…"
            : state.status === "success" ? "CART RESTORED"
            : state.status === "already-paid" ? "ORDER ALREADY COMPLETED"
            : "COULDN'T RESTORE CART"}
        </h1>
        <p className="font-mono text-sm text-subtle">{state.message}</p>
        {state.status !== "loading" && state.status !== "success" && (
          <button
            data-testid="resume-back-button"
            onClick={() => navigate("/")}
            className="mt-6 bg-ink text-paper px-6 h-11 font-mono text-xs uppercase tracking-widest shadow-hard hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-transform"
          >
            Back to Catalog →
          </button>
        )}
      </div>
    </div>
  );
}
